import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/hooks/useAuth';
import { getProductImage } from '@/lib/productMedia';
import { getStoreCapabilities } from '@/lib/accountType';
import { getMainSiteUrl } from '@/lib/domain';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { loadMyLinksStats, trackMyLinksClick, trackMyLinksView, type MyLinksStats } from '@/lib/mylinksStats';

// ─────────────────────────────────────────────────────────────
// /mylinks — page « lien en bio » de chaque commerce.
// Profil, liens rapides, réseaux sociaux, produits, services,
// annonces, événements et articles de blog, en un seul lien.
// ─────────────────────────────────────────────────────────────

interface ProductRow {
  id: number;
  name: string;
  description: string | null;
  price: number | null;
  currency: string | null;
  discount_enabled: boolean | null;
  discount_price: number | null;
  media: unknown;
}

interface ServiceRow {
  id: number;
  titre: string;
  description: string | null;
  prix: number | null;
  product_image: string | null;
  ville: string | null;
}

interface AnnonceRow {
  id: number;
  titre: string;
  description: string | null;
  prix: number | null;
  image: string | null;
  ville: string | null;
  type: string | null;
  disponible: boolean | null;
}

interface EventRow {
  id: number;
  title: string | null;
  description: string | null;
  start_datetime: string | null;
  id_haut: string | null;
  price: number | null;
  currency: string | null;
}

interface ArticleRow {
  id: number;
  title: string | null;
  slug: string | null;
  excerpt: string | null;
  cover_image: string | null;
  published_at: string | null;
  created_at: string | null;
  status: string | null;
}

function formatPrice(value: number | null | undefined, currency: string): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 'Gratuit';
  return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`;
}

function firstSentence(text: string | null | undefined, max = 110): string {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max).trimEnd()}…`;
}

interface MyLinksButton {
  label: string;
  url: string;
  icon?: string;
}

interface MyLinksRow {
  cover_url: string | null;
  headline: string | null;
  featured_kind: string | null;
  featured_id: number | null;
  buttons: unknown;
}

interface Featured {
  kind: 'product' | 'service' | 'annonce';
  id: number;
  title: string;
  description: string | null;
  price: number | null;
  currency: string;
  image: string | null;
  href: string;
}

interface OfferDetail {
  kind: 'product' | 'service' | 'annonce' | 'event' | 'article';
  id: number;
  title: string;
  description: string | null;
  price: number | null;
  currency: string;
  image: string | null;
  extra: string | null;
  href: string;
}

/** Image robuste : si le fichier est introuvable, on masque proprement. */
function SafeImage({
  src,
  alt,
  title,
  className,
  fallback,
}: {
  src: string;
  alt: string;
  title?: string;
  className: string;
  fallback: ReactNode;
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (!src || broken) return <>{fallback}</>;
  return (
    <img
      src={src}
      alt={alt}
      title={title || alt}
      className={className}
      onError={() => setBroken(true)}
    />
  );
}

export default function MyLinksPage() {
  const { tenant, theme } = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState<ProductRow[]>([]);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [annonces, setAnnonces] = useState<AnnonceRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [headline, setHeadline] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [buttons, setButtons] = useState<MyLinksButton[]>([]);
  const [featured, setFeatured] = useState<Featured | null>(null);
  const [detail, setDetail] = useState<OfferDetail | null>(null);
  const [stats, setStats] = useState<MyLinksStats | null>(null);
  const [statsOpen, setStatsOpen] = useState(false);

  const caps = getStoreCapabilities(tenant?.type);
  const currency = tenant?.monaie || 'EUR';

  useEffect(() => {
    if (!tenant) return;
    let active = true;

    (async () => {
      setLoading(true);
      try {
        // Personnalisation choisie par le commerçant.
        let cfg: MyLinksRow | null = null;
        try {
          const cfgRes = await supabase
            .from('merchant_mylinks')
            .select('cover_url, headline, featured_kind, featured_id, buttons')
            .eq('merchant_id', tenant.id)
            .maybeSingle();
          cfg = (cfgRes.data || null) as MyLinksRow | null;
        } catch {
          cfg = null;
        }
        if (active && cfg) {
          setCoverUrl(String(cfg.cover_url || ''));
          setHeadline(String(cfg.headline || ''));
          setButtons(
            Array.isArray(cfg.buttons)
              ? (cfg.buttons as MyLinksButton[]).filter((b) => b && String(b.label || '').trim() && String(b.url || '').trim())
              : [],
          );
        }

        const [prodRes, servRes, annRes, artRes] = await Promise.all([
          supabase
            .from('product_items')
            .select('id, name, description, price, currency, discount_enabled, discount_price, media')
            .eq('idcommerce', tenant.id)
            .neq('status', 'draft')
            .order('created_at', { ascending: false })
            .limit(8),
          supabase
            .from('nospartenairesservices')
            .select('id, titre, description, prix, product_image, ville')
            .eq('owner', tenant.id)
            .order('id', { ascending: false })
            .limit(8),
          supabase
            .from('annonce')
            .select('id, titre, description, prix, image, ville, type, disponible')
            .eq('idvendeur', tenant.id)
            .order('id', { ascending: false })
            .limit(8),
          supabase
            .from('blog_articles')
            .select('id, title, slug, excerpt, cover_image, published_at, created_at, status')
            .eq('idcommerce', tenant.id)
            .order('created_at', { ascending: false })
            .limit(6),
        ]);

        if (!active) return;
        setProducts((prodRes.data || []) as ProductRow[]);
        setServices((servRes.data || []) as ServiceRow[]);
        setAnnonces((annRes.data || []) as AnnonceRow[]);
        setArticles(
          ((artRes.data || []) as ArticleRow[]).filter((a) => String(a.status || '').toLowerCase() !== 'draft'),
        );

        // Prochains événements du commerce.
        try {
          const { data: evRows } = await supabase
            .from('events')
            .select('id, title, description, start_datetime, id_haut, price, currency')
            .eq('id_user', String(tenant.id))
            .eq('status', 1)
            .gte('start_datetime', new Date().toISOString())
            .order('start_datetime', { ascending: true })
            .limit(3);
          if (active) setEvents((evRows || []) as EventRow[]);
        } catch {
          if (active) setEvents([]);
        }

        // Offre mise en avant.
        const kind = String(cfg?.featured_kind || '');
        const fid = Number(cfg?.featured_id) || 0;
        if (kind && fid > 0) {
          if (kind === 'product') {
            const { data } = await supabase
              .from('product_items')
              .select('id, name, description, price, currency, discount_enabled, discount_price, media')
              .eq('id', fid)
              .maybeSingle();
            const p = data as ProductRow | null;
            if (active && p) {
              setFeatured({
                kind: 'product',
                id: p.id,
                title: p.name,
                description: p.description,
                price: p.discount_enabled && p.discount_price != null ? p.discount_price : p.price,
                currency: p.currency || currency,
                image: getProductImage(p.media),
                href: `/product/${p.id}`,
              });
            }
          } else if (kind === 'service') {
            const { data } = await supabase
              .from('nospartenairesservices')
              .select('id, titre, description, prix, product_image')
              .eq('id', fid)
              .maybeSingle();
            const s = data as ServiceRow | null;
            if (active && s) {
              setFeatured({
                kind: 'service',
                id: s.id,
                title: s.titre,
                description: s.description,
                price: s.prix,
                currency,
                image: s.product_image,
                href: `/service/${s.id}`,
              });
            }
          } else if (kind === 'annonce') {
            const { data } = await supabase
              .from('annonce')
              .select('id, titre, description, prix, image')
              .eq('id', fid)
              .maybeSingle();
            const a = data as AnnonceRow | null;
            if (active && a) {
              setFeatured({
                kind: 'annonce',
                id: a.id,
                title: a.titre,
                description: a.description,
                price: a.prix,
                currency,
                image: a.image,
                href: `/annonce/${a.id}`,
              });
            }
          }
        }
      } catch {
        if (active) {
          setProducts([]);
          setServices([]);
          setAnnonces([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [tenant, currency]);

  // Capteur de trafic : chaque visite de /mylinks est enregistrée
  // (IP + pays + ville résolus côté serveur).
  useEffect(() => {
    if (!tenant?.id) return;
    void trackMyLinksView({
      id: tenant.id,
      nomcommerce: tenant.nomcommerce,
      name: tenant.name,
      user_name: tenant.user_name,
    });
  }, [tenant?.id, tenant?.nomcommerce, tenant?.name, tenant?.user_name]);

  const loadStats = useCallback(async () => {
    if (!tenant?.id) return;
    try {
      setStats(await loadMyLinksStats(tenant.id, 30));
    } catch {
      setStats(null);
    }
  }, [tenant?.id]);

  useEffect(() => {
    if (user?.id && tenant?.id && Number(user.id) === Number(tenant.id)) void loadStats();
  }, [user?.id, tenant?.id, loadStats]);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${window.location.pathname}`;
  }, []);

  const socials = useMemo(() => {
    if (!tenant) return [];
    return [
      { label: 'Facebook', icon: 'ri-facebook-circle-fill', url: tenant.lienfacebook },
      { label: 'Instagram', icon: 'ri-instagram-line', url: tenant.lieninstagram },
      { label: 'TikTok', icon: 'ri-tiktok-fill', url: tenant.lientiktok },
      { label: 'X', icon: 'ri-twitter-x-fill', url: tenant.lientwitter },
      { label: 'LinkedIn', icon: 'ri-linkedin-box-fill', url: tenant.lienlinkedin },
      { label: 'YouTube', icon: 'ri-youtube-fill', url: tenant.lienyoutube },
    ].filter((s) => String(s.url || '').trim() !== '');
  }, [tenant]);

  if (!tenant) return null;

  const displayName = tenant.nomcommerce || tenant.name || tenant.user_name;
  const isOwner = Boolean(user?.id && tenant?.id && Number(user.id) === Number(tenant.id));
  const initial = (displayName || 'Z').trim().charAt(0).toUpperCase();
  const cover = resolveMediaUrl(coverUrl || tenant.imageaboutus || '');
  const logo = resolveMediaUrl(tenant.image || '');
  const whatsappNumber = String(tenant.telephone || '').replace(/[^0-9]/g, '');
  const encodedShareUrl = encodeURIComponent(shareUrl);

  const tenantRef = { id: tenant.id, nomcommerce: tenant.nomcommerce, name: tenant.name, user_name: tenant.user_name };

  const openOffer = (offer: OfferDetail) => {
    setDetail(offer);
    void trackMyLinksClick({
      tenant: tenantRef,
      kind: offer.kind === 'event' || offer.kind === 'article' ? 'site' : offer.kind,
      id: offer.id,
      title: offer.title,
    });
  };

  const openProduct = (p: ProductRow) => openOffer({
    kind: 'product', id: p.id, title: p.name, description: p.description,
    price: p.discount_enabled && p.discount_price != null ? p.discount_price : p.price,
    currency: p.currency || currency, image: getProductImage(p.media), extra: null, href: `/product/${p.id}`,
  });

  const openService = (s: ServiceRow) => openOffer({
    kind: 'service', id: s.id, title: s.titre, description: s.description,
    price: s.prix, currency, image: s.product_image, extra: s.ville, href: `/service/${s.id}`,
  });

  const openAnnonce = (a: AnnonceRow) => openOffer({
    kind: 'annonce', id: a.id, title: a.titre, description: a.description,
    price: a.prix, currency, image: a.image, extra: a.ville, href: `/annonce/${a.id}`,
  });

  const openEvent = (e: EventRow) => openOffer({
    kind: 'event', id: e.id, title: e.title || 'Événement', description: e.description,
    price: e.price, currency: e.currency || currency, image: e.id_haut,
    extra: e.start_datetime ? new Date(e.start_datetime).toLocaleDateString('fr-FR') : null,
    href: `/events/${e.id}`,
  });

  const openArticle = (a: ArticleRow) => openOffer({
    kind: 'article', id: a.id, title: a.title || 'Article', description: a.excerpt,
    price: null, currency, image: a.cover_image,
    extra: a.published_at ? new Date(a.published_at).toLocaleDateString('fr-FR') : null,
    href: `/blog/${a.slug || a.id}`,
  });

  const openFeatured = () => {
    if (!featured) return;
    openOffer({
      kind: featured.kind, id: featured.id, title: featured.title, description: featured.description,
      price: featured.price, currency: featured.currency, image: featured.image, extra: null, href: featured.href,
    });
  };

  const copyLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: displayName, url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      /* partage annulé */
    }
  };

  return (
    <div className="w-full min-h-screen bg-background-50">
      <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-5 md:py-10">

        {/* ── Profil ── */}
        <div className="rounded-xl border border-background-200/70 overflow-hidden bg-background-50">
          <div className="relative h-32 sm:h-40 md:h-48 w-full overflow-hidden">
            <SafeImage
              src={cover}
              alt={`Bannière ${displayName}`}
              title={`${displayName} — ${theme?.navTitle || displayName}`}
              className="w-full h-full object-cover object-top"
              fallback={<div className="w-full h-full bg-gradient-to-br from-primary-100 via-background-100 to-accent-100"></div>}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/5 to-transparent"></div>
          </div>

          <div className="px-4 sm:px-5 md:px-6 pb-5">
            <div className="-mt-11 flex items-end justify-between gap-3 flex-wrap">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-4 border-background-50 bg-background-100 flex items-center justify-center flex-shrink-0">
                <SafeImage
                  src={logo}
                  alt={`Logo ${displayName}`}
                  title={displayName}
                  className="w-full h-full object-contain p-1 bg-background-50"
                  fallback={<span className="text-2xl md:text-3xl font-bold text-primary-600 font-heading">{initial}</span>}
                />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Partager sur Facebook"
                  aria-label="Partager sur Facebook"
                  className="w-9 h-9 rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100 hover:text-primary-700 transition-colors flex items-center justify-center no-underline"
                >
                  <i className="ri-facebook-circle-fill text-base"></i>
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${displayName} — ${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Partager sur WhatsApp"
                  aria-label="Partager sur WhatsApp"
                  className="w-9 h-9 rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100 hover:text-accent-700 transition-colors flex items-center justify-center no-underline"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                </a>
                <button
                  type="button"
                  onClick={copyLink}
                  className="flex items-center gap-1.5 px-3.5 h-9 rounded-full bg-background-50 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={copied ? 'ri-check-line text-primary-600' : 'ri-share-forward-line'}></i>
                  {copied ? 'Copié !' : 'Partager'}
                </button>
              </div>
            </div>

            <div className="mt-3">
              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 break-words">{displayName}</h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-foreground-500">
                {tenant.Ville && (
                  <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{tenant.Ville}</span>
                )}
                {tenant.type && (
                  <span className="flex items-center gap-1"><i className="ri-store-2-line"></i>{tenant.type}</span>
                )}
                {tenant.user_name && (
                  <span className="flex items-center gap-1"><i className="ri-at-line"></i>{tenant.user_name}</span>
                )}
              </div>
              {headline && (
                <p className="text-sm font-medium text-primary-700 mt-2 leading-snug">{headline}</p>
              )}
              {tenant.description && (
                <p className="text-sm text-foreground-600 mt-2.5 leading-relaxed">{tenant.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* ── Statistiques (commerçant) ── */}
        {isOwner && stats && (
          <div className="mt-4 rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
            <button
              type="button"
              onClick={() => setStatsOpen((v) => !v)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer hover:bg-background-100/60 transition-colors"
            >
              <span className="w-8 h-8 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-line-chart-line"></i>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-foreground-900">Statistiques de ce lien</span>
                <span className="block text-[11px] text-foreground-500">
                  {stats.weekViews} visite{stats.weekViews > 1 ? 's' : ''} sur 7 jours · {stats.totalViews} au total
                </span>
              </span>
              <i className={`${statsOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
            </button>
            {statsOpen && (
              <div className="px-4 pb-4 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: "Aujourd'hui", value: stats.todayViews, color: 'text-foreground-950' },
                    { label: '7 jours', value: stats.weekViews, color: 'text-foreground-950' },
                    { label: 'Visiteurs uniques', value: stats.uniqueVisitors, color: 'text-secondary-700' },
                    { label: 'Taux de clic', value: `${stats.conversion} %`, color: 'text-accent-700' },
                  ].map((kpi) => (
                    <div key={kpi.label} className="rounded-md bg-background-100 px-3 py-2 min-w-0">
                      <p className="text-[10px] uppercase tracking-wide text-foreground-500 truncate">{kpi.label}</p>
                      <p className={`text-lg font-bold ${kpi.color}`}>{kpi.value}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-600 uppercase tracking-wide mb-1.5">Sources</p>
                    {stats.sources.length === 0 ? (
                      <p className="text-xs text-foreground-400">Aucune source enregistrée.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {stats.sources.map((s) => {
                          const max = stats.sources[0]?.count || 1;
                          return (
                            <div key={s.label} className="flex items-center gap-2">
                              <span className="w-16 text-[11px] text-foreground-600 capitalize truncate">{s.label}</span>
                              <span className="flex-1 h-1.5 rounded-full bg-background-100 overflow-hidden">
                                <span className="block h-full rounded-full bg-secondary-500" style={{ width: `${Math.round((s.count / max) * 100)}%` }}></span>
                              </span>
                              <span className="text-[11px] text-foreground-500 w-8 text-right">{s.count}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-600 uppercase tracking-wide mb-1.5">Pays</p>
                    {stats.countries.length === 0 ? (
                      <p className="text-xs text-foreground-400">Pas encore de géolocalisation.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {stats.countries.map((c) => (
                          <span key={c.label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium">
                            <i className="ri-earth-line"></i>{c.label} · {c.count}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] font-semibold text-foreground-600 uppercase tracking-wide mb-1.5 mt-3">Villes</p>
                    {stats.cities.length === 0 ? (
                      <p className="text-xs text-foreground-400">Pas encore de ville.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {stats.cities.map((c) => (
                          <span key={c.label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[11px] font-medium">
                            <i className="ri-map-pin-line"></i>{c.label} · {c.count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => void loadStats()}
                  className="flex items-center gap-1.5 text-[11px] font-medium text-primary-700 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>Actualiser
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Offre à la une ── */}
        {featured && (
          <div className="mt-4 rounded-xl border border-primary-200/70 overflow-hidden bg-background-50">
            <div className="px-4 pt-3 pb-1 flex items-center gap-1.5">
              <i className="ri-flashlight-line text-primary-600 text-sm"></i>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-primary-700">Offre à la une</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 p-3 pt-2">
              <div className="w-full sm:w-32 h-36 sm:h-32 rounded-md overflow-hidden bg-background-100 flex items-center justify-center flex-shrink-0">
                <SafeImage
                  src={resolveMediaUrl(featured.image || '')}
                  alt={featured.title}
                  title={`${featured.title} — ${displayName}`}
                  className="w-full h-full object-cover object-top"
                  fallback={<i className="ri-image-line text-2xl text-foreground-300"></i>}
                />
              </div>
              <div className="min-w-0 flex-1 flex flex-col">
                <h2 className="text-base font-semibold text-foreground-950 break-words">{featured.title}</h2>
                <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{firstSentence(featured.description, 120)}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold text-primary-700">{formatPrice(featured.price, featured.currency)}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
                    {featured.kind === 'product' ? 'Produit' : featured.kind === 'service' ? 'Service' : 'Annonce'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={openFeatured}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors rounded-full px-3.5 py-2 w-fit whitespace-nowrap cursor-pointer"
                >
                  Voir l&apos;offre<i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Liens rapides ── */}
        <div className="mt-4 space-y-2.5">
          <Link
            to="/"
            onClick={() => void trackMyLinksClick({ tenant: tenantRef, kind: 'site', id: 0, title: 'Site de la boutique' })}
            className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors no-underline"
          >
            <span className="w-9 h-9 rounded-full bg-background-50/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-global-line text-lg"></i>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold">Visiter le site de la boutique</span>
              <span className="block text-[11px] text-background-50/80">Catalogue, réservation et contact</span>
            </span>
            <i className="ri-arrow-right-up-line text-base flex-shrink-0"></i>
          </Link>

          <div className="grid grid-cols-2 gap-2.5">
            {caps.products && (
              <Link to="/produits" className="flex items-center gap-2.5 px-3 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors no-underline min-w-0">
                <span className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shopping-bag-3-line"></i>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-foreground-900 truncate">Nos produits</span>
                  <span className="block text-[10px] text-foreground-500 truncate">Catalogue</span>
                </span>
              </Link>
            )}
            {caps.services && (
              <Link to="/services" className="flex items-center gap-2.5 px-3 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors no-underline min-w-0">
                <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-service-line"></i>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-foreground-900 truncate">Nos services</span>
                  <span className="block text-[10px] text-foreground-500 truncate">Prestations</span>
                </span>
              </Link>
            )}
            <Link to="/annonces" className="flex items-center gap-2.5 px-3 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors no-underline min-w-0">
              <span className="w-9 h-9 rounded-full bg-background-200/70 text-foreground-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-megaphone-line"></i>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-foreground-900 truncate">Annonces</span>
                <span className="block text-[10px] text-foreground-500 truncate">Publications</span>
              </span>
            </Link>
            <Link to="/events" className="flex items-center gap-2.5 px-3 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors no-underline min-w-0">
              <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-event-line"></i>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-foreground-900 truncate">Événements</span>
                <span className="block text-[10px] text-foreground-500 truncate">Agenda</span>
              </span>
            </Link>
            <Link to="/blog" className="flex items-center gap-2.5 px-3 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors no-underline min-w-0">
              <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-article-line"></i>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-foreground-900 truncate">Blog</span>
                <span className="block text-[10px] text-foreground-500 truncate">Articles</span>
              </span>
            </Link>
            {whatsappNumber && (
              <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors no-underline min-w-0">
                <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line"></i>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-foreground-900 truncate">WhatsApp</span>
                  <span className="block text-[10px] text-foreground-500 truncate">Écrire</span>
                </span>
              </a>
            )}
          </div>
        </div>

        {/* ── Boutons libres ── */}
        {buttons.length > 0 && (
          <div className="mt-2.5 space-y-2.5">
            {buttons.map((b, i) => (
              <a
                key={i}
                href={/^https?:\/\//i.test(b.url) ? b.url : `https://${b.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-accent-50 border border-accent-100 hover:bg-accent-100 transition-colors no-underline"
              >
                <span className="w-9 h-9 rounded-full bg-accent-500 text-background-50 flex items-center justify-center flex-shrink-0">
                  <i className={`${b.icon || 'ri-link'} text-base`}></i>
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium text-foreground-900 truncate">{b.label}</span>
                <i className="ri-arrow-right-up-line text-foreground-400 flex-shrink-0"></i>
              </a>
            ))}
          </div>
        )}

        {/* ── Réseaux sociaux ── */}
        {socials.length > 0 && (
          <div className="mt-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">Suivez-nous</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.label}
                  className="group flex items-center gap-2.5 px-3 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/40 transition-colors no-underline min-w-0"
                >
                  <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${s.icon} text-base`}></i>
                  </span>
                  <span className="min-w-0 flex-1 text-sm font-medium text-foreground-900 truncate">{s.label}</span>
                  <i className="ri-arrow-right-up-line text-foreground-400 flex-shrink-0"></i>
                </a>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : (
          <>
            {/* ── Événements ── */}
            {events.length > 0 && (
              <section className="mt-7">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                    <i className="ri-calendar-event-line text-accent-600"></i>Événements
                  </h2>
                  <Link to="/events" className="text-xs text-primary-700 hover:text-primary-600 no-underline whitespace-nowrap">Tout voir</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {events.map((ev) => {
                    const date = ev.start_datetime ? new Date(ev.start_datetime) : null;
                    const day = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('fr-FR', { day: '2-digit' }) : '--';
                    const month = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '') : '---';
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => openEvent(ev)}
                        className="flex items-center gap-3 p-3 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 transition-colors text-left cursor-pointer w-full min-w-0"
                      >
                        <span className="w-12 h-12 rounded-md bg-accent-50 border border-accent-100 text-accent-700 flex flex-col items-center justify-center flex-shrink-0 leading-none">
                          <span className="text-sm font-bold">{day}</span>
                          <span className="text-[9px] font-semibold uppercase mt-0.5">{month}</span>
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-foreground-900 truncate">{ev.title || 'Événement'}</span>
                          <span className="block text-[11px] text-foreground-500 truncate">
                            {Number(ev.price || 0) > 0 ? formatPrice(ev.price, ev.currency || currency) : 'Entrée libre'}
                          </span>
                        </span>
                        <i className="ri-arrow-right-s-line text-foreground-400 flex-shrink-0"></i>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Produits ── */}
            {products.length > 0 && (
              <section className="mt-7">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                    <i className="ri-shopping-bag-3-line text-secondary-600"></i>Produits
                  </h2>
                  <Link to="/produits" className="text-xs text-primary-700 hover:text-primary-600 no-underline whitespace-nowrap">Tout voir</Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {products.map((p) => {
                    const image = resolveMediaUrl(getProductImage(p.media));
                    const price = p.discount_enabled && p.discount_price != null ? p.discount_price : p.price;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => openProduct(p)}
                        className="group text-left rounded-lg border border-background-200/70 overflow-hidden bg-background-50 hover:border-primary-300 transition-colors cursor-pointer min-w-0"
                      >
                        <div className="w-full h-28 md:h-32 bg-background-100 overflow-hidden">
                          <SafeImage
                            src={image}
                            alt={p.name}
                            title={`${p.name} — ${displayName}`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            fallback={<div className="w-full h-full flex items-center justify-center"><i className="ri-image-line text-xl text-foreground-300"></i></div>}
                          />
                        </div>
                        <div className="p-2.5">
                          <div className="text-xs font-medium text-foreground-900 truncate">{p.name}</div>
                          <div className="text-[11px] font-semibold text-primary-700 mt-0.5">
                            {formatPrice(price, p.currency || currency)}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Services ── */}
            {services.length > 0 && (
              <section className="mt-7">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                    <i className="ri-service-line text-accent-600"></i>Services
                  </h2>
                  <Link to="/services" className="text-xs text-primary-700 hover:text-primary-600 no-underline whitespace-nowrap">Tout voir</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {services.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => openService(s)}
                      className="flex items-center gap-3 p-2.5 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 transition-colors text-left cursor-pointer w-full min-w-0"
                    >
                      <div className="w-14 h-14 rounded-md bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                        <SafeImage
                          src={resolveMediaUrl(s.product_image || '')}
                          alt={s.titre}
                          title={`${s.titre} — ${displayName}`}
                          className="w-full h-full object-cover object-top"
                          fallback={<i className="ri-service-line text-lg text-foreground-300"></i>}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground-900 truncate">{s.titre}</div>
                        <div className="text-[11px] text-foreground-500 truncate">{firstSentence(s.description, 90)}</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs font-semibold text-accent-700">{formatPrice(s.prix, currency)}</span>
                          {s.ville && (
                            <span className="text-[10px] text-foreground-400 flex items-center gap-0.5">
                              <i className="ri-map-pin-line"></i>{s.ville}
                            </span>
                          )}
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-400 flex-shrink-0"></i>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* ── Annonces ── */}
            {annonces.length > 0 && (
              <section className="mt-7">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                    <i className="ri-megaphone-line text-primary-600"></i>Annonces
                  </h2>
                  <Link to="/annonces" className="text-xs text-primary-700 hover:text-primary-600 no-underline whitespace-nowrap">Tout voir</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {annonces.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => openAnnonce(a)}
                      className="flex items-center gap-3 p-2.5 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 transition-colors text-left cursor-pointer w-full min-w-0"
                    >
                      <div className="w-14 h-14 rounded-md bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                        <SafeImage
                          src={resolveMediaUrl(a.image || '')}
                          alt={a.titre}
                          title={`${a.titre} — ${displayName}`}
                          className="w-full h-full object-cover object-top"
                          fallback={<i className="ri-megaphone-line text-lg text-foreground-300"></i>}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground-900 truncate">{a.titre}</div>
                        <div className="text-[11px] text-foreground-500 truncate">{firstSentence(a.description, 90)}</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs font-semibold text-primary-700">{formatPrice(a.prix, currency)}</span>
                          {a.ville && (
                            <span className="text-[10px] text-foreground-400 flex items-center gap-0.5">
                              <i className="ri-map-pin-line"></i>{a.ville}
                            </span>
                          )}
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-400 flex-shrink-0"></i>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* ── Articles de blog ── */}
            {articles.length > 0 && (
              <section className="mt-7">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                    <i className="ri-article-line text-primary-600"></i>Articles
                  </h2>
                  <Link to="/blog" className="text-xs text-primary-700 hover:text-primary-600 no-underline whitespace-nowrap">Tout voir</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {articles.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => openArticle(a)}
                      className="flex items-start gap-3 p-2.5 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 transition-colors text-left cursor-pointer w-full min-w-0"
                    >
                      <div className="w-16 h-16 rounded-md bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                        <SafeImage
                          src={resolveMediaUrl(a.cover_image || '')}
                          alt={a.title || 'Article'}
                          title={`${a.title || 'Article'} — ${displayName}`}
                          className="w-full h-full object-cover object-top"
                          fallback={<i className="ri-article-line text-lg text-foreground-300"></i>}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground-900 line-clamp-2">{a.title || 'Article'}</div>
                        <div className="text-[11px] text-foreground-500 line-clamp-2 mt-0.5">{firstSentence(a.excerpt, 90)}</div>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-400 flex-shrink-0 mt-4"></i>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {products.length === 0 && services.length === 0 && annonces.length === 0 && events.length === 0 && articles.length === 0 && (
              <div className="mt-8 rounded-lg border border-background-200/70 bg-background-50 px-4 py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-store-2-line text-xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-600">Aucune publication pour le moment.</p>
                <p className="text-xs text-foreground-400 mt-1">Visitez le site pour découvrir la boutique.</p>
              </div>
            )}
          </>
        )}

        {/* ── Détail d'une offre ── */}
        {detail && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={() => setDetail(null)}>
            <div
              className="w-full sm:max-w-lg max-h-[90dvh] overflow-y-auto bg-background-50 rounded-t-xl sm:rounded-xl border border-background-200/70"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-44 sm:h-48 w-full bg-background-100 overflow-hidden">
                <SafeImage
                  src={resolveMediaUrl(detail.image || '')}
                  alt={detail.title}
                  title={`${detail.title} — ${displayName}`}
                  className="w-full h-full object-cover object-top"
                  fallback={<div className="w-full h-full flex items-center justify-center"><i className="ri-image-line text-3xl text-foreground-300"></i></div>}
                />
                <button
                  type="button"
                  onClick={() => setDetail(null)}
                  aria-label="Fermer"
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-background-50/90 text-foreground-700 flex items-center justify-center cursor-pointer hover:bg-background-50 transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
                <span className="absolute bottom-3 left-3 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-700">
                  {detail.kind === 'product' ? 'Produit'
                    : detail.kind === 'service' ? 'Service'
                      : detail.kind === 'annonce' ? 'Annonce'
                        : detail.kind === 'event' ? 'Événement' : 'Article'}
                </span>
              </div>
              <div className="p-5">
                <h2 className="text-lg font-bold font-heading text-foreground-950 break-words">{detail.title}</h2>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  {detail.price != null && Number(detail.price) > 0 && (
                    <span className="text-sm font-bold text-primary-700">{formatPrice(detail.price, detail.currency)}</span>
                  )}
                  {detail.extra && (
                    <span className="text-[11px] text-foreground-500 flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>{detail.extra}
                    </span>
                  )}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mt-3 whitespace-pre-line">
                  {detail.description ? firstSentence(detail.description, 1200) : 'Aucune description fournie pour cette publication.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mt-5">
                  <button
                    type="button"
                    onClick={() => navigate(detail.href)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-global-line"></i>Voir sur le site
                  </button>
                  <button
                    type="button"
                    onClick={() => setDetail(null)}
                    className="px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Pied ── */}
        <div className="mt-10 pt-5 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-[11px] text-foreground-400 text-center sm:text-left">
            {displayName} · {tenant.Ville || 'Zifek'}
          </p>
          <a
            href={getMainSiteUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-foreground-500 hover:text-primary-700 no-underline flex items-center gap-1"
          >
            Propulsé par <span className="font-semibold">Zifek</span>
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </div>
      </div>
    </div>
  );
}